/**
 * Single source of truth for member preference labels in Profile → Preferences
 * and in the post-register continue-register wizard. Tag order matches the profile UI.
 */

export interface ProfilePreferenceCategory {
  id: string;
  question: string;
  tags: string[];
  defaultSelected: string[];
}

/**
 * Member type options (Profile / continue-register step 1). IDs → accountTypeId in API; align with `/member/types` if needed.
 *
 * The dropdown itself is driven by the API catalog (`GET /member/types` → `sharedOptions` where
 * `type = 'memberType'`); this static list is the offline fallback used for label resolution.
 * It mirrors the seeded catalog, including the Service Provider types that the Services
 * marketplace lists (DJ, Band, …, Chef, Bartender).
 */
export const MEMBER_ACCOUNT_TYPE_OPTIONS: { id: number; label: string }[] = [
  { id: 1, label: "Clubber" },
  { id: 2, label: "Critic (Food & Beverage)" },
  { id: 3, label: "Event Planner" },
  { id: 4, label: "Influencer" },
  { id: 5, label: "Member" },
  { id: 6, label: "Promoter" },
  { id: 7, label: "Socialite" },
  // Service Provider types — the same catalog the `/services` page filters by.
  { id: 8, label: "DJ" },
  { id: 9, label: "Band" },
  { id: 10, label: "Dancer" },
  { id: 11, label: "Musician" },
  { id: 12, label: "Model" },
  { id: 13, label: "MC" },
  { id: 14, label: "Singer" },
  { id: 15, label: "Comedian" },
  { id: 16, label: "Magician" },
  { id: 17, label: "Photographer" },
  { id: 18, label: "Chef" },
  { id: 19, label: "Bartender" },
];

/** Same categories and tag lists as UserProfile → Preferences. */
export const PROFILE_PREFERENCE_CATEGORIES: ProfilePreferenceCategory[] = [
  {
    id: "venues",
    question: "What type of venues you like?",
    tags: ["Bar", "Beach Bar", "Cabaret", "Cruise", "Event Space", "Hotel", "Rooftop", "Lounge", "Nightclub", "Pool", "Restaurant"],
    defaultSelected: ["Restaurant"],
  },
  {
    id: "amenities",
    question: "What amenities you look for in a venue?",
    tags: ["Bowling", "Business Center", "Cigar", "Coat Check", "Darts", "Handicap Accessible", "Karaoke", "Dance Floor", "Hookah", "Hotel Rooms", "Pool Tables", "TVs", "WiFi", "Outdoor Smoking"],
    defaultSelected: [],
  },
  {
    id: "drinks",
    question: "What is your favorite drink?",
    tags: ["Beer", "Champagne", "Cognac", "Gin", "Rosé", "Rum", "Scotch", "Alcohol Free", "Cocktails"],
    defaultSelected: [],
  },
  {
    id: "cuisines",
    question: "What type of cuisines you like?",
    tags: ["Finnish", "Fusion", "Greek", "Indian", "Indonesian", "Italian", "Japanese", "American", "Chinese", "Continental", "Cuban", "Egyptian", "Lebanese", "Malaysian"],
    defaultSelected: [],
  },
  {
    id: "djs",
    question: "Who is your favorite DJs?",
    tags: ["Above & Beyond", "Afrojack", "Alan Walker", "Alesso", "Aly & Fila", "Coone", "Da Tweekaz", "Daddy's Groove", "Daft Punk"],
    defaultSelected: [],
  },
  {
    id: "spending",
    question: "What is your average spending?",
    tags: ["$1-25", "$26-49", "$50-99", "$100-149", "$150+"],
    defaultSelected: [],
  },
  {
    id: "crowd",
    question: "What type of crowd you feel comfortable with?",
    tags: ["International", "LGBT", "Older Crowd", "Couples", "Fashionable", "Hipster", "Singles", "Sophisticated", "Sugar Baby", "Sweet Daddy"],
    defaultSelected: [],
  },
  {
    id: "music",
    question: "What type of music you like?",
    tags: ["Easy Listening", "EDM", "House", "International", "Jazz", "Open Format", "Indie Rock", "Classics", "Dance", "Hip-Hop", "Latin Music"],
    defaultSelected: [],
  },
  {
    id: "atmosphere",
    question: "Favorite venue atmosphere?",
    tags: ["Art Deco", "Chill Out", "Cozy", "Industrial", "Loud", "Modern", "Trendy", "Casual", "Retro", "Romantic", "Theme"],
    defaultSelected: [],
  },
  {
    id: "entertainment",
    question: "What kind of Entertainment you like?",
    tags: ["Burlesque", "Comedians", "Musicians", "Open Mic", "Singers", "Dancers", "DJs", "Bands"],
    defaultSelected: [],
  },
  {
    id: "dietary",
    question: "Choose your dietaries options",
    tags: ["Gluten Free", "Signature Dish", "Spicy", "Vegan", "Vegetarian", "Healthy", "Hormone Free", "Organic"],
    defaultSelected: [],
  },
  {
    id: "payments",
    question: "What Payments method do you use?",
    tags: ["Android Pay", "Apple Pay", "Bitcoin", "Credit Cards", "Cash"],
    defaultSelected: ["Credit Cards"],
  },
];

